import React, { useId, useState } from "react";
import { LOST_REASONS, LOST_OTHER } from "../constants/lostReasons";

/**
 * The list of Lost reasons, shared by every Lost menu (the Lost button
 * dropdown, the Seller Leads row menu and the Prospects "Lost, why?" dialog).
 *
 * Picking "Other" asks for the agent's own words before anything is marked
 * Lost (Christian Gravias, Monday 13375284987, 26 Oct 2026: "When an agent
 * clicks other there must be a text box where they write why. Dont want them
 * clicking other and thats it"). The backend refuses Other without them too.
 *
 * Props:
 *  - onPick(reason, note): note is '' for every reason but Other
 *  - reasons: defaults to the lead/appraisal LOST_REASONS
 *  - compact: the small dropdown styling, else the dialog's full-width rows
 */
export default function LostReasonPicker({ onPick, reasons = LOST_REASONS, compact = false }) {
  const [askingWhy, setAskingWhy] = useState(false);
  const [note, setNote] = useState("");
  const noteId = useId();
  const trimmed = note.trim();

  if (askingWhy) {
    return (
      <form
        className={compact ? "px-3 py-2 space-y-2" : "space-y-2"}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) onPick(LOST_OTHER, trimmed);
        }}
      >
        <label className="block text-xs font-semibold text-gray-700" htmlFor={noteId}>
          Other: why was it lost?
        </label>
        <textarea
          id={noteId}
          autoFocus
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Selling privately to a neighbour"
          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => { setAskingWhy(false); setNote(""); }}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!trimmed}
            className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 disabled:opacity-50"
          >
            Mark as Lost
          </button>
        </div>
      </form>
    );
  }

  return reasons.map((r) => (
    <button
      key={r.value}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (r.value === LOST_OTHER) setAskingWhy(true);
        else onPick(r.value, "");
      }}
      className={
        compact
          ? "w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          : "w-full text-left px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"
      }
    >
      {r.label}
    </button>
  ));
}
